import { Helmet } from "react-helmet-async";

const SITE_URL = "https://mawered.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/landing/og-cover.png`;

const LOCALE_MAP = {
  fr: "fr_FR",
  en: "en_US",
  ar: "ar_AR",
};

/**
 * SEO tags per page — title, description, canonical, OG, Twitter, JSON-LD.
 *
 * Do NOT duplicate these statically in public/index.html — Helmet adds them
 * at runtime and duplicates confuse Google's canonical selection.
 *
 * @param {string}       path       Canonical path, e.g. "/", "/privacy"
 * @param {string}       lang       BCP-47 code (fr|en|ar). Sets og:locale.
 * @param {string}       type       og:type — "website" (default) or "article"
 * @param {Array}        alternates [{lang, href}] hreflang links
 * @param {object|Array} jsonLd     Structured data to inject as JSON-LD
 * @param {boolean}      noindex    Suppress indexing (login, admin pages)
 */
const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  lang = "fr",
  type = "website",
  alternates = [],
  jsonLd = null,
  noindex = false,
}) => {
  const canonical = `${SITE_URL}${path}`;
  const ogLocale = LOCALE_MAP[lang] ?? "fr_FR";
  const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Mawered" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* hreflang alternates */}
      {alternates.map(({ lang: l, href }) => (
        <link key={l} rel="alternate" hrefLang={l} href={href} />
      ))}

      {/* JSON-LD structured data */}
      {jsonLdItems.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
